import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductCatalogService } from '@yol-digital/ms-client';
import { TabItem, tabSelectorIconMap, TabSelectorKeyEnum } from './tab-selector.component';

@Injectable({ providedIn: 'root' })
export class TabSelectorService {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public tabItems = signal<TabItem[]>([]);
  public activeTab = signal(0);
  public activeSubTab = signal(0);
  public activeTabName = computed(() => this.tabItems()?.[this.activeTab()]?.tabName);
  public activeSubTabName = computed(() => this.tabItems()?.[this.activeTab()]?.subtabs?.[this.activeSubTab()]);
  private readonly order = [TabSelectorKeyEnum.mobile, TabSelectorKeyEnum.internet, TabSelectorKeyEnum.tv];
  private initialFragment = this.route.snapshot.fragment;

  constructor() {
    effect(() => {
      if (this.tabItems()?.length > 0) {
        this.applyFragmentToTabs();
      }
    });
  }

  private applyFragmentToTabs() {
    const fragment = this.initialFragment || this.route.snapshot.fragment;
    if (!fragment) return;

    this.setActiveTabFromFragment(fragment);
    // Clear initial fragment after first use to prevent reapplication
    this.initialFragment = null;
  }

  private setActiveSubtabByName(subtabName: string) {
    const tabItems = this.tabItems();
    if (!tabItems || tabItems.length === 0 || this.activeTab() === -1) return;

    const currentTab = tabItems[this.activeTab()];
    if (!currentTab?.subtabs) return;

    if (subtabName) {
      const subtabs = currentTab.subtabs;
      const subtabIndex = subtabs.findIndex(subtab => subtab.toLowerCase() === subtabName.toLowerCase());

      const targetSubtabIndex = subtabIndex !== -1 ? subtabIndex : 0;
      if (this.activeSubTab() !== targetSubtabIndex) {
        this.activeSubTab.set(targetSubtabIndex);
      }
    } else if (this.activeSubTab() !== 0) {
      this.activeSubTab.set(0);
    }
  }

  private setActiveTabFromFragment(fragment: string): void {
    const tabItems = this.tabItems();
    if (!tabItems || tabItems.length === 0) return;

    const [tabName, subtabName] = fragment.toLowerCase().split('-');

    this.setActiveTabByName(tabName);
    if (subtabName) {
      this.setActiveSubtabByName(subtabName);
    }
  }

  private setActiveTabByName(tabName: string) {
    const tabItems = this.tabItems();
    if (!tabItems || tabItems.length === 0) return;

    const tabIndex = tabItems.findIndex(tab => tab.tabName.toLowerCase() === tabName.toLowerCase());
    if (tabIndex !== -1) {
      if (this.activeTab() !== tabIndex) {
        this.activeTab.set(tabIndex);
      }
    }
  }

  public updateFragment() {
    const tab = this.activeTabName()?.toLowerCase();
    const sub = this.activeSubTabName()?.toLowerCase();
    let fragment = tab;
    if (sub) fragment += `-${sub}`;
    this.router.navigate([], { fragment, replaceUrl: true, relativeTo: this.route });
  }

  public getTabAndSubTabs(
    items: {
      family: ProductCatalogService.CatalogResponse['productSpecFamily'];
      subCategories?: string[];
      hasPromotion?: boolean;
    }[],
    includePromotions = false
  ): TabItem[] {
    if (!items) return null;

    const uniqueFamilies = [...new Set(items.map(item => item.family.toLowerCase() as TabSelectorKeyEnum))];

    return uniqueFamilies
      .sort((a, b) => this.order.indexOf(a) - this.order.indexOf(b))
      .map(family => this.createTabItem(items, family, includePromotions));
  }

  private createTabItem(
    items: { family: string; subCategories?: string[]; hasPromotion?: boolean }[],
    family: TabSelectorKeyEnum,
    includePromotions: boolean
  ): TabItem {
    const filteredItems = items.filter(item => item.family.toLowerCase() === family);
    const hasSubCategories = filteredItems.some(({ subCategories }) => subCategories?.length);

    const subtabs =
      (hasSubCategories && [
        ...(includePromotions && family === 'mobile' && filteredItems.some(item => item.hasPromotion)
          ? ['promotions']
          : []),
        ...new Set(
          filteredItems.flatMap(item => item.subCategories.map(subCategory => subCategory.toLowerCase()) || [])
        ),
      ]) ||
      [];

    return {
      tabName: family,
      icon: tabSelectorIconMap[family],
      textKey: `${family}_btn`,
      subtabs,
    };
  }
}
